import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { Reveal } from "../components/Motion";
import { LOCATION_PAGES } from "../lib/locationPages";
import { PRODUCTS } from "../lib/data";

export default function LocationPage() {
    const { slug: paramSlug } = useParams();
    // Routes are hard-coded literals (no :slug param), so fall back to pathname
    const slug = paramSlug || window.location.pathname.replace(/^\//, "");
    const cfg = LOCATION_PAGES[slug];
    if (!cfg) return <Navigate to="/" replace />;

    const schema = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "LocalBusiness",
                "@id": `https://thepuropack.com/${slug}#business`,
                name: "The Puro Pack",
                description: cfg.meta,
                url: `https://thepuropack.com/${slug}`,
                image: "https://thepuropack.com/logo-512.png",
                address: { "@type": "PostalAddress", addressLocality: "Bangalore", addressRegion: "Karnataka", addressCountry: "IN" },
                areaServed: cfg.searchTerms.some(s => /india/i.test(s)) ? "IN" : "Bangalore",
            },
            {
                "@type": "WebPage",
                "@id": `https://thepuropack.com/${slug}`,
                name: cfg.seoTitle,
                description: cfg.meta,
                breadcrumb: {
                    "@type": "BreadcrumbList",
                    itemListElement: [
                        { "@type": "ListItem", position: 1, name: "Home", item: "https://thepuropack.com/" },
                        { "@type": "ListItem", position: 2, name: cfg.title, item: `https://thepuropack.com/${slug}` },
                    ],
                },
            },
        ],
    };

    return (
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10" data-testid={`location-${slug}`}>
            <Seo title={cfg.seoTitle.replace(" | The Puro Pack", "")} description={cfg.meta} path={`/${slug}`} canonicalOverride={cfg.canonicalTo ? `https://thepuropack.com${cfg.canonicalTo}` : undefined} schema={schema} />
            <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: cfg.title, to: `/${slug}` }]} />

            <section className="pt-14 pb-20 grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-8">
                    <div className="font-mono text-xs uppercase tracking-widest text-clay mb-6">{cfg.eyebrow}</div>
                    <h1 className="font-serif text-6xl lg:text-8xl leading-[0.95] tracking-tight text-forest">{cfg.h1}</h1>
                    <p className="mt-8 text-inkgreen/85 leading-relaxed text-lg max-w-2xl" data-testid="location-intro">
                        {cfg.intro.split("**").map((chunk, i) => i % 2 === 1 ? <strong key={i} className="text-forest">{chunk}</strong> : chunk)}
                    </p>
                </div>
                <div className="lg:col-span-4 lg:pt-20">
                    <div className="border-t border-forest/30 pt-6">
                        <div className="font-mono text-xs uppercase tracking-widest text-stone mb-4">Why work with us</div>
                        <ul className="space-y-3 text-inkgreen/85">
                            {cfg.bullets.map((b, i) => (
                                <li key={i} className="flex gap-3">
                                    <span className="font-mono text-clay mt-1">→</span>
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <Reveal>
                <section className="border-t border-forest/30 py-16">
                    <div className="font-mono text-xs uppercase tracking-widest text-stone mb-6">[ Product Range ]</div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {PRODUCTS.map((p) => (
                            <Link key={p.slug} to={`/products/${p.slug}`} className="block group border border-forest/30 hover:border-forest transition-colors">
                                <div className="image-frame aspect-[4/3]">
                                    <img src={p.image} alt={p.title} loading="lazy" />
                                </div>
                                <div className="p-5 flex justify-between items-center">
                                    <div>
                                        <div className="font-serif text-2xl text-forest">{p.title}</div>
                                        <div className="text-xs font-mono uppercase text-stone mt-1">{p.highlights[0].value}</div>
                                    </div>
                                    <ArrowUpRight className="text-forest group-hover:text-clay" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            </Reveal>

            <section className="bg-forest text-bone -mx-6 lg:-mx-10 px-6 lg:px-10 py-16 my-16 flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
                <h2 className="font-serif text-3xl md:text-5xl tracking-tight max-w-3xl">Ready to test our bowls in your kitchen?</h2>
                <Link to="/contact" className="btn-pill border-bone text-bone hover:bg-bone hover:text-forest" data-testid="location-cta">
                    Request a Sample <ArrowUpRight className="w-4 h-4" />
                </Link>
            </section>
        </div>
    );
}
